import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ArtistUpdateModuleMutation,
  ArtistUpdateModuleMutationVariables,
  ConsoleDigitalMembershipCardQuery,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages, generatePath } = useRouter()
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('consoleDigitalMembershipCard')

  const { data, loading, error, refetch } =
    useQuery<ConsoleDigitalMembershipCardQuery>(QUERY)

  const [changeDigitalMembershipCard, { loading: changingSettings }] =
    useMutation<
      ArtistUpdateModuleMutation,
      ArtistUpdateModuleMutationVariables
    >(ARTIST_CHANGE_DIGITAL_MEMBERSHIP_CARD_MUTATION)

  return {
    state: {
      user: user ?? null,
      artistId: user?.uid ?? '',
      loading,
      error,
      artist: data?.artist ?? null,
      favoriteMembers: data?.artistFavoriteMembers ?? [],
      enableDigitalMembershipCard:
        data?.artist?.settings?.features?.subscription
          ?.enableDigitalMembershipCard ?? false,
      changingSettings: changingSettings,
    },
    actions: {
      onEnableSettings: async () => {
        await changeDigitalMembershipCard({
          variables: {
            inputs: {
              digitalMembershipCard: true,
            },
          },
        }).catch(error => {
          showError(error)
          throw error
        })
        await refetch()
        showMessage(t('updateSettingsSuccess'), 'success')
      },
      onDisableSettings: async () => {
        await changeDigitalMembershipCard({
          variables: {
            inputs: {
              digitalMembershipCard: false,
            },
          },
        }).catch(error => {
          showError(error)
          throw error
        })
        await refetch()
        showMessage(t('updateSettingsSuccess'), 'success')
      },
    },
  }
})

const QUERY = gql`
  query ConsoleDigitalMembershipCard {
    artist {
      id
      displayName
      digitalMembershipCard {
        cardDesignPath
        displayAreaColor
        textColor
      }
      settings {
        features {
          subscription {
            enableDigitalMembershipCard
          }
        }
      }
    }
    artistFavoriteMembers {
      id
      name
      avatar {
        uri
        smallUri
      }
      orderNumber
      digitalMembershipCard {
        cardDesignPath
        displayAreaColor
        textColor
      }
    }
  }
`
const ARTIST_CHANGE_DIGITAL_MEMBERSHIP_CARD_MUTATION = gql`
  mutation artistUpdateModule($inputs: ArtistUpdateModuleInput!) {
    artistUpdateModule(inputs: $inputs) {
      id
      displayName
      settings {
        features {
          subscription {
            enableDigitalMembershipCard
          }
        }
      }
    }
  }
`
